import type { AnchoredRow } from '../types/anchoredRowTypes'

export interface ManualClusterSyncRowIdParts {
  chapterId: string
  clusterId: string
  storeId: string
  rowNumber: number
}

export interface ManualAnchorStoreFilterOptions {
  chapterId: string
  clusterId?: string
  storeId?: string
  manualAnchorCount?: number
}

const MANUAL_SYNC_PREFIX = 'manual_cluster_sync'
const STRUCTURAL_EDIT_ROW_PREFIXES = ['row-insert-', 'row-split-'] as const

const isStructuralEditRow = (row: AnchoredRow): boolean => (
  STRUCTURAL_EDIT_ROW_PREFIXES.some(prefix => row.id.startsWith(prefix))
)

const countActiveMappings = (row: AnchoredRow): number => {
  const mappings = Array.isArray(row.unitMappings) ? row.unitMappings : []
  return mappings.filter(mapping => !mapping?.excluded).length
}

const parseTimestamp = (value: unknown): number => {
  // mutation-ignore-next-line equivalent: blank strings parse to NaN and still fall back to zero.
  if (typeof value !== 'string' || value.trim().length === 0) {
    return 0
  }
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const parseManualClusterSyncRowId = (
  rowId: string
): ManualClusterSyncRowIdParts | null => {
  if (!rowId) {
    return null
  }

  const parts = rowId.split('::')
  if (parts[0] !== MANUAL_SYNC_PREFIX) {
    return null
  }

  if (parts.length === 5) {
    const rowNumber = Number.parseInt(parts[4], 10)
    if (!Number.isFinite(rowNumber)) {
      return null
    }
    return {
      chapterId: parts[1],
      clusterId: parts[2],
      storeId: parts[3],
      rowNumber
    }
  }

  if (parts.length === 4) {
    const rowNumber = Number.parseInt(parts[3], 10)
    if (!Number.isFinite(rowNumber)) {
      return null
    }
    return {
      chapterId: parts[1],
      clusterId: parts[2],
      storeId: 'default',
      rowNumber
    }
  }

  return null
}

export const filterRowsByManualAnchorStore = (
  rows: AnchoredRow[],
  options: ManualAnchorStoreFilterOptions
): AnchoredRow[] => {
  const chapterId = options.chapterId
  const clusterId = options.clusterId || ''
  const storeId = options.storeId || ''

  return rows.filter(row => {
    const parsed = parseManualClusterSyncRowId(row.id)
    if (!parsed || parsed.chapterId !== chapterId) {
      return false
    }
    if (clusterId && parsed.clusterId !== clusterId) {
      return false
    }
    if (!storeId) {
      return true
    }
    return parsed.storeId === storeId
  })
}

const sortAnchoredRows = (rows: AnchoredRow[]): AnchoredRow[] => {
  return [...rows].sort((left, right) => {
    const leftPosition = Number.isFinite(left.position) ? left.position : 0
    const rightPosition = Number.isFinite(right.position) ? right.position : 0
    if (leftPosition !== rightPosition) {
      return leftPosition - rightPosition
    }
    if (left.labelNumber !== right.labelNumber) {
      return left.labelNumber - right.labelNumber
    }
    return left.id.localeCompare(right.id)
  })
}

const dedupeAnchoredRowsByPosition = (rows: AnchoredRow[]): AnchoredRow[] => {
  const selectedByPosition = new Map<number, AnchoredRow>()

  rows.forEach((row, index) => {
    const position = Number.isFinite(row.position) ? row.position : (index + 1)
    const existing = selectedByPosition.get(position)
    if (!existing) {
      selectedByPosition.set(position, row)
      return
    }

    const existingMappingCount = countActiveMappings(existing)
    const candidateMappingCount = countActiveMappings(row)
    if (candidateMappingCount > existingMappingCount) {
      selectedByPosition.set(position, row)
      return
    }
    if (candidateMappingCount < existingMappingCount) {
      return
    }

    const existingUpdated = parseTimestamp((existing as any).updatedAt)
    const candidateUpdated = parseTimestamp((row as any).updatedAt)
    if (candidateUpdated > existingUpdated) {
      selectedByPosition.set(position, row)
      return
    }

    if (candidateUpdated === existingUpdated && row.id > existing.id) {
      selectedByPosition.set(position, row)
    }
  })

  return Array.from(selectedByPosition.values())
}

export const resolveStudioAnchorRows = (
  rows: AnchoredRow[],
  options: ManualAnchorStoreFilterOptions
): AnchoredRow[] => {
  const manualScopedRows = dedupeAnchoredRowsByPosition(filterRowsByManualAnchorStore(rows, options))
  const chapterRows = rows.filter(row => row.chapterId === options.chapterId)
  const nonManualRows = chapterRows.filter(row => !parseManualClusterSyncRowId(row.id))
  const structuralEditRows = nonManualRows.filter(isStructuralEditRow)
  const importedRows = nonManualRows.filter(row => !isStructuralEditRow(row))
  // Imported row stores historically used position as a row key, so retain
  // their compatibility de-duplication. Ledger Safe insert/split rows are
  // explicit identities, however, and may deliberately share that legacy
  // ordering coordinate with a neighbouring imported row.
  const chapterNonManualRows = [
    ...dedupeAnchoredRowsByPosition(importedRows),
    ...structuralEditRows
  ]
  const dedupedChapterRows = dedupeAnchoredRowsByPosition(chapterRows)
  const hasManualAnchors = (options.manualAnchorCount || 0) > 0
  const hasExplicitStoreSelection = typeof options.storeId === 'string' && options.storeId.trim().length > 0

  if (hasExplicitStoreSelection) {
    return sortAnchoredRows(manualScopedRows)
  }
  if (hasManualAnchors && manualScopedRows.length > 0) {
    return sortAnchoredRows(manualScopedRows)
  }
  if (chapterNonManualRows.length > 0) {
    return sortAnchoredRows(chapterNonManualRows)
  }
  if (manualScopedRows.length > 0) {
    return sortAnchoredRows(manualScopedRows)
  }

  return sortAnchoredRows(dedupedChapterRows)
}

export const collectManualAnchorStoreIds = (
  rows: AnchoredRow[],
  chapterId: string,
  clusterId?: string
): string[] => {
  const unique = new Set<string>()
  rows.forEach(row => {
    const parsed = parseManualClusterSyncRowId(row.id)
    if (!parsed || parsed.chapterId !== chapterId) {
      return
    }
    if (clusterId && parsed.clusterId !== clusterId) {
      return
    }
    unique.add(parsed.storeId)
  })
  return Array.from(unique).sort((left, right) => left.localeCompare(right))
}
